import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, CircleOff } from "lucide-react";

import { Card } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Skeleton } from "~/components/ui/skeleton";
import {
  StatBlock,
  strongestAreas,
} from "~/components/wheel/results/results-summary";
import { Wheel } from "~/components/wheel/wheel";
import { coachSharedWheelQueryOptions } from "~/lib/queries/wheelShareQuery";
import { selectWeakAreas } from "~/lib/shared/wheel";
import { formatMonthDay } from "~/lib/utils/timezone";

/**
 * A shared Wheel as the coach sees it. Only the engagement's coach can open
 * it, and only while the client shares it: the page never links to the
 * public results, which would keep working after the client stops sharing.
 */
export const Route = createFileRoute(
  "/_user/my-coaching/clients/programs/$engagementId/wheel/$shareId",
)({
  staticData: { title: "Wheel of Life" },
  component: SharedWheelPage,
});

function SharedWheelPage() {
  const { engagementId, shareId } = Route.useParams();
  const { data, isLoading } = useQuery(
    coachSharedWheelQueryOptions(engagementId, shareId),
  );

  return (
    <div className="space-y-4">
      <Link
        to="/my-coaching/clients/programs/$engagementId"
        params={{ engagementId }}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="size-4" />
        Overview
      </Link>

      {isLoading ? (
        <Skeleton className="h-96 w-full rounded-2xl" />
      ) : !data ? (
        <EmptyState
          icon={CircleOff}
          title="This Wheel isn't shared with you"
          description="It may have been shared and then withdrawn."
        />
      ) : (
        <Card className="gap-6 p-6">
          <header>
            <h1 className="text-xl font-medium text-gray-900">
              {data.clientFirstName}'s Wheel of Life
            </h1>
            <p className="text-sm text-gray-500" suppressHydrationWarning>
              Shared {formatMonthDay(data.sharedAt)}
            </p>
          </header>
          <div className="grid gap-8 md:grid-cols-2">
            <div>
              <div className="flex justify-center">
                <Wheel
                  mode="static"
                  labels={data.labels}
                  scores={data.scores}
                  size={340}
                />
              </div>
              <StatBlock
                labels={data.labels}
                scores={data.scores}
                strongest={strongestAreas(data.scores)}
                weak={selectWeakAreas(data.scores)}
              />
            </div>
            <section aria-labelledby="shared-wheel-reflection">
              <h2
                id="shared-wheel-reflection"
                className="text-sm font-medium text-gray-900"
              >
                The reflection {data.clientFirstName} received
              </h2>
              <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap text-gray-700">
                {data.reflection ??
                  "No reflection was generated for this Wheel."}
              </p>
            </section>
          </div>
        </Card>
      )}
    </div>
  );
}
